import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';
import { UserRole } from '../Services/AuthService';

// Componentes para páginas específicas por rol
import UserDashboard from '../Pages/User/UserDashboard';
import Members from '../Pages/User/Members';
import ProjectQuiz from '../Pages/User/ProjectQuiz';
import SectionReview from '../Pages/User/SectionReview';
import ArchitectDashboard from '../Pages/Architect/ArchitectDashboard';
import ProjectDetail from '../Pages/Architect/ProjectDetail';
import ProjectReport from '../Pages/Architect/ProjectReport';
import AdminDashboard from '../Pages/Admin/AdminDashboard';
import QuizConfiguration from '../Pages/Admin/QuizConfiguration';
import AllProjectsAdmin from '../Pages/Admin/AllProjectsAdmin';
import AdminProjectDetail from '../Pages/Admin/AdminProjectDetail';
import AdminReportEditor from '../Pages/Admin/AdminReportEditor';
import Question from '../Questions/Question';
import ArchitectManagement from '../Pages/Admin/ArchitectManagement';
import SubscriptionManagement from '../Pages/Admin/SubscriptionManagement';

// Componentes para gestión de usuarios (Arquitecto)
// Removed unused imports after cleaning up architect pages

// Componente para rutas protegidas por rol
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole: UserRole;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { hasRole } = useAuth();
  
  if (!hasRole(requiredRole)) {
    // Redirigir a la página principal según el rol del usuario
    return <Navigate to="/" />;
  }
  
  return <>{children}</>;
};

// Componente principal para las rutas del dashboard
const DashboardRoutes: React.FC = () => {
  const { user } = useAuth();
  
  // Si no hay usuario, redirigir al login
  if (!user?.isAuthenticated) {
    return <Navigate to="/" />;
  }
  
  return (
    <Routes>
      {/* Ruta raíz que redirige según el rol */}
      <Route path="/" element={
        <>
          {user.role === UserRole.SUPERADMIN && <Navigate to="/admin/dashboard" />}
          {user.role === UserRole.ARCHITECT && <Navigate to="/architect" />}
          {user.role === UserRole.USER && <Navigate to="/user/dashboard" />}
        </>
      } />
      
      {/* Rutas para Usuario */}
      <Route path="/user/dashboard" element={
        <ProtectedRoute requiredRole={UserRole.USER}>
          <UserDashboard />
        </ProtectedRoute>
      } />

      <Route path="/user/members" element={
        <ProtectedRoute requiredRole={UserRole.USER}>
          <Members />
        </ProtectedRoute>
      } />

      <Route path="/user/project/:projectId/group/:groupId" element={
        <ProtectedRoute requiredRole={UserRole.USER}>
          <ProjectQuiz />
        </ProtectedRoute>
      } />

      <Route path="/user/project/:projectId/group/:groupId/section/:sectionIndex" element={
        <ProtectedRoute requiredRole={UserRole.USER}>
          <ProjectQuiz />
        </ProtectedRoute>
      } />

      <Route path="/user/project/:projectId/group/:groupId/section/:sectionIndex/review" element={
        <ProtectedRoute requiredRole={UserRole.USER}>
          <SectionReview />
        </ProtectedRoute>
      } />

      <Route path="/question" element={
        <ProtectedRoute requiredRole={UserRole.USER}>
          <Question />
        </ProtectedRoute>
      } />
      
      {/* Rutas para Arquitecto */}
      <Route path="/architect" element={
        <ProtectedRoute requiredRole={UserRole.ARCHITECT}>
          <ArchitectDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/architect/dashboard" element={
        <ProtectedRoute requiredRole={UserRole.ARCHITECT}>
          <ArchitectDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/architect/project/:projectId" element={
        <ProtectedRoute requiredRole={UserRole.ARCHITECT}>
          <ProjectDetail />
        </ProtectedRoute>
      } />

      <Route path="/architect/project/:projectId/report" element={
        <ProtectedRoute requiredRole={UserRole.ARCHITECT}>
          <ProjectReport />
        </ProtectedRoute>
      } />

      {/* Rutas para SuperAdmin */}
      <Route path="/admin/dashboard" element={
        <ProtectedRoute requiredRole={UserRole.SUPERADMIN}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/architects" element={
        <ProtectedRoute requiredRole={UserRole.SUPERADMIN}>
          <ArchitectManagement />
        </ProtectedRoute>
      } />

      <Route path="/admin/subscriptions" element={
        <ProtectedRoute requiredRole={UserRole.SUPERADMIN}>
          <SubscriptionManagement />
        </ProtectedRoute>
      } />

      <Route path="/admin/users" element={
        <ProtectedRoute requiredRole={UserRole.SUPERADMIN}>
          <div>Gestión de Todos los Usuarios</div>
        </ProtectedRoute>
      } />
      
      <Route path="/configurador" element={
        <ProtectedRoute requiredRole={UserRole.SUPERADMIN}>
          <QuizConfiguration />
        </ProtectedRoute>
      } />

      <Route path="/admin/projects" element={
        <ProtectedRoute requiredRole={UserRole.SUPERADMIN}>
          <AllProjectsAdmin />
        </ProtectedRoute>
      } />

      <Route path="/admin/projects/:projectId" element={
        <ProtectedRoute requiredRole={UserRole.SUPERADMIN}>
          <AdminProjectDetail />
        </ProtectedRoute>
      } />

      <Route path="/admin/reports/:id/edit" element={
        <ProtectedRoute requiredRole={UserRole.SUPERADMIN}>
          <AdminReportEditor />
        </ProtectedRoute>
      } />

      {/* Ruta por defecto (no encontrada) */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default DashboardRoutes;